import { useSearchParams } from "react-router";
import {
  DiffMode,
  RecipeComplianceReportLabel,
  RecipeComplianceReportShiftAnchor,
  useGetRecipeComplianceReportDailyQuery,
  useGetRecipeComplianceReportMonthlyQuery,
} from "src/store/api/generatedApi";
import { RecipeComplianceEmptyState } from "components/strategyDashboard/compliance/RecipeComplianceEmptyState";
import { RecipeComplianceReport } from "components/strategyDashboard/compliance/RecipeComplianceReport";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { ArtefactShell, ArtefactState } from "./ArtefactShell";

const asDiffMode = (value: string | null): DiffMode | undefined =>
  value === "mass" || value === "fraction" ? value : undefined;

const asShiftAnchor = (
  value: string | null
): RecipeComplianceReportShiftAnchor => (value === "last" ? "last" : "first");

const asReportLabel = (
  value: string | null
): RecipeComplianceReportLabel | undefined =>
  value === "daily_shift" || value === "morning_meeting" ? value : undefined;

/** Renders a tenant's recipe-compliance report for headless capture. */
export const RecipeComplianceArtefactPage = () => {
  const [searchParams] = useSearchParams();
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(true);

  const anchorDate = searchParams.get("date") ?? "";
  const isMonthly = searchParams.get("timeAggregation") === "monthly";
  const diffMode = asDiffMode(searchParams.get("diffMode"));

  const dailyQuery = useGetRecipeComplianceReportDailyQuery(
    {
      tenantName,
      anchorDate,
      shiftAnchor: asShiftAnchor(searchParams.get("shiftAnchor")),
      reportLabel: asReportLabel(searchParams.get("reportLabel")),
    },
    { skip: isMonthly || anchorDate === "" }
  );
  const monthlyQuery = useGetRecipeComplianceReportMonthlyQuery(
    { tenantName, anchorDate },
    { skip: !isMonthly || anchorDate === "" }
  );

  const { data, isError } = isMonthly ? monthlyQuery : dailyQuery;

  const state: ArtefactState =
    anchorDate === "" || isError ? "error" : data ? "ready" : "loading";
  const resolvedDiffMode: DiffMode =
    diffMode ?? data?.default_diff_mode ?? "mass";

  return (
    <ArtefactShell state={state} rawData={data}>
      {data != null &&
        (data.breakdown != null ? (
          <RecipeComplianceReport
            breakdown={data.breakdown}
            diffMode={resolvedDiffMode}
            variant="static"
            t={t}
            units={units}
          />
        ) : (
          <RecipeComplianceEmptyState
            title={data.title}
            period={isMonthly ? "monthly" : "daily"}
            windowStart={data.window_start}
          />
        ))}
    </ArtefactShell>
  );
};
